import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, Platform } from '../components/Icons.jsx';
import { useContentFeed } from '../hooks/useContentFeed.js';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'generating', label: 'Generating' },
  { id: 'ready', label: 'Ready' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Drafts' },
  { id: 'failed', label: 'Failed' },
];

export const Content = ({ navigate, openPost }) => {
  const { content, counts: feedCounts, loading, loadingMore, error, hasMore, loadMore, hydrateContentItem, refreshContent } = useContentFeed();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [filter, setFilter] = useState(() => routeContentFilter(location.search));
  const [view, setView] = useState('grid');
  const [query, setQuery] = useState('');
  const sentinelRef = useRef(null);
  const refreshContentRef = useRef(refreshContent);
  refreshContentRef.current = refreshContent;

  const normalizedQuery = query.trim().toLowerCase();
  const items = content
    .filter(item => !normalizedQuery || `${item.title} ${item.type} ${item.platform}`.toLowerCase().includes(normalizedQuery));
  const counts = FILTERS.reduce((acc, item) => {
    const localCount = item.id === 'all' ? content.length : content.filter(post => filterMatchesContent(post, item.id)).length;
    acc[item.id] = Number.isFinite(Number(feedCounts?.[item.id])) ? Number(feedCounts[item.id]) : localCount;
    return acc;
  }, {});

  useEffect(() => {
    const routeFilter = routeContentFilter(location.search);
    setFilter(routeFilter);
    refreshContentRef.current?.({ status: routeFilter });
  }, [location.search]);

  function selectFilter(id) {
    setFilter(id);
    const params = new URLSearchParams(location.search);
    if (id === 'all') params.delete('filter');
    else params.set('filter', id);
    const query = params.toString();
    routerNavigate(query ? `/content?${query}` : '/content', { replace: true });
  }

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        loadMore();
      }
    }, { rootMargin: '320px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadMore]);

  return (
    <div className="page col content-page-v2" style={{ gap: 16 }}>
      <div className="row between content-toolbar-v2">
        <div className="dt-tabs">
          {FILTERS.map(item => (
            <button key={item.id} className={'dt-tab ' + (filter === item.id ? 'is-active' : '')} onClick={() => selectFilter(item.id)}>
              {item.label} <span style={{ opacity: 0.65 }}>{counts[item.id]}</span>
            </button>
          ))}
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="search-input content-search-v2">
            <Icon name="search" size={14} />
            <input placeholder="Search content..." value={query} onChange={event => setQuery(event.target.value)} />
          </div>
          <div className="content-view-toggle">
            <button className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}><Icon name="grid" size={14} /></button>
            <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}><Icon name="menu" size={14} /></button>
          </div>
        </div>
      </div>

      {error && <div className="auth-error"><Icon name="alert" size={14} /> {error}</div>}
      {loading ? (
        <div className="card content-empty-v2"><Icon name="refresh" size={24} /><p className="body" style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading backend content...</p></div>
      ) : items.length === 0 ? (
        <EmptyContent onCreate={() => navigate('create')} query={query} />
      ) : view === 'grid' ? (
        <div className="content-grid-v2">
          {items.map((item, index) => (
            <ContentCard key={item.id || `${item.title}-${index}`} item={item} openPost={openPost} hydrateContentItem={hydrateContentItem} priority={index < 4} />
          ))}
        </div>
      ) : (
        <div className="card content-list-v2" style={{ padding: 0 }}>
          {items.map((item, index) => (
            <button key={item.id || `${item.title}-${index}`} className="content-list-row-v2" onClick={() => openPost && openPost(item.raw || item)}>
              <ContentListThumb item={item} />
              <div className="col" style={{ flex: 1, minWidth: 0, gap: 2, textAlign: 'left' }}>
                <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                <div className="caption">{item.type} · {item.date}{item.publishMeta ? ` · ${item.publishMeta}` : ''}</div>
                {item.status === 'generating' && <div className="caption">{generationStageLabel(item.generationStage)} · {clampProgress(item.generationProgress)}%</div>}
              </div>
              <TargetStatusPills item={item} compact />
              <MetricPair metrics={item.metrics} status={item.status} compact />
              <span className={'chip hide-mobile ' + statusClass(item.status)}>{statusLabel(item.status)}</span>
              <Icon name="chev_r" size={14} />
            </button>
          ))}
        </div>
      )}

      {!loading && (hasMore || loadingMore) && (
        <div ref={sentinelRef} className="content-load-more">
          {loadingMore ? (
            <span className="caption"><Icon name="refresh" size={13} /> Loading more posts…</span>
          ) : hasMore ? (
            <button className="btn btn-secondary btn-sm" onClick={() => loadMore()}>Load more</button>
          ) : null}
        </div>
      )}
    </div>
  );
};

function filterMatchesContent(item, filter) {
  if (filter === 'all') return true;
  return item.status === filter;
}

function routeContentFilter(search) {
  const routeFilter = new URLSearchParams(search).get('filter');
  return routeFilter && FILTERS.some(item => item.id === routeFilter) ? routeFilter : 'all';
}

function ContentCard({ item, openPost, hydrateContentItem, priority = false }) {
  const generating = item.status === 'generating';
  const actualImages = Array.isArray(item.imageUrls) && item.imageUrls.length ? item.imageUrls : (item.imageUrl ? [item.imageUrl] : []);
  const fallbackImages = actualImages.length ? [] : fallbackCarouselImages(item);
  const images = actualImages.length ? actualImages : fallbackImages;
  const [imageIndex, setImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(() => new Set());
  const [hydratingMedia, setHydratingMedia] = useState(false);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const safeIndex = Math.min(imageIndex, Math.max(0, images.length - 1));
  const imageKey = images.join('|');
  const currentImage = images[safeIndex];
  const imageLoaded = currentImage ? loadedImages.has(currentImage) : false;
  const mediaCount = Math.max(Number(item.mediaPreview?.count || 0), images.length);
  const hasCarousel = mediaCount > 1;
  const hasLoadedCarousel = actualImages.length > 1 || fallbackImages.length > 1;
  const canHydrateCarousel = hasCarousel && actualImages.length > 0 && actualImages.length < mediaCount && item.id && typeof hydrateContentItem === 'function';
  const canBrowseCarousel = hasLoadedCarousel || canHydrateCarousel;

  useEffect(() => {
    setImageIndex(current => Math.min(current, Math.max(0, images.length - 1)));
  }, [imageKey, images.length]);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const cancel = preloadCarouselNeighbors(images, safeIndex);
    return cancel;
  }, [imageKey, safeIndex]);

  function hydrateFullCarousel() {
    if (!canHydrateCarousel || hydratingMedia) return;
    setHydratingMedia(true);
    hydrateContentItem(item.id).catch(() => {}).finally(() => setHydratingMedia(false));
  }

  function showImage(index, event) {
    event?.stopPropagation();
    event?.preventDefault();
    if (!images.length) return;
    if (!hasLoadedCarousel && canHydrateCarousel) {
      setImageIndex((index + mediaCount) % mediaCount);
      hydrateFullCarousel();
      return;
    }
    const next = (index + images.length) % images.length;
    setImageIndex(next);
  }

  function handleImageLoaded() {
    if (!currentImage) return;
    setLoadedImages(previous => {
      if (previous.has(currentImage)) return previous;
      const next = new Set(previous);
      next.add(currentImage);
      return next;
    });
  }

  function handleTouchStart(event) {
    if (!canBrowseCarousel) return;
    touchStartX.current = event.touches?.[0]?.clientX ?? null;
    touchDeltaX.current = 0;
    if (hasLoadedCarousel) preloadCarouselNeighbors(images, safeIndex);
    else hydrateFullCarousel();
  }

  function handleTouchMove(event) {
    if (touchStartX.current === null) return;
    touchDeltaX.current = (event.touches?.[0]?.clientX ?? touchStartX.current) - touchStartX.current;
  }

  function handleTouchEnd(event) {
    if (touchStartX.current === null) return;
    const delta = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    if (Math.abs(delta) < 40) return;
    showImage(delta < 0 ? safeIndex + 1 : safeIndex - 1, event);
  }

  function handleCardKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPost && openPost(item.raw || item);
      return;
    }
    if (!canBrowseCarousel) return;
    if (event.key === 'ArrowLeft') showImage(safeIndex - 1, event);
    if (event.key === 'ArrowRight') showImage(safeIndex + 1, event);
  }

  return (
    <div
      className="content-card-v2"
      role="button"
      tabIndex={0}
      onClick={() => openPost && openPost(item.raw || item)}
      onKeyDown={handleCardKeyDown}
      onMouseEnter={() => hasLoadedCarousel ? preloadCarouselNeighbors(images, safeIndex) : hydrateFullCarousel()}
    >
      <div
        className="content-thumb-v2"
        aria-roledescription={hasCarousel ? 'carousel' : undefined}
        aria-label={hasCarousel ? `Slide ${safeIndex + 1} of ${mediaCount}` : undefined}
        aria-busy={hydratingMedia ? 'true' : undefined}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {generating ? (
          <div className="content-thumb-art is-generating"><div className="content-skeleton-orb" /></div>
        ) : images.length ? (
          <>
            <img
              className={'content-thumb-img ' + (imageLoaded ? 'is-loaded' : 'is-loading')}
              src={currentImage}
              alt=""
              loading={priority ? 'eager' : 'lazy'}
              fetchPriority={priority ? 'high' : 'auto'}
              decoding="async"
              width="400"
              height="400"
              aria-label={`Slide ${safeIndex + 1} of ${mediaCount || images.length}`}
              onLoad={handleImageLoaded}
            />
            {!imageLoaded && <span className="content-thumb-shimmer" aria-hidden="true" />}
            {canBrowseCarousel ? (
              <>
                <button
                  type="button"
                  className="content-thumb-nav prev"
                  onClick={event => showImage(safeIndex - 1, event)}
                  aria-label="Previous image"
                ><Icon name="chev_l" size={14} /></button>
                <button
                  type="button"
                  className="content-thumb-nav next"
                  onClick={event => showImage(safeIndex + 1, event)}
                  aria-label="Next image"
                ><Icon name="chev_r" size={14} /></button>
                {hasLoadedCarousel && (
                  <div className="content-thumb-dots">
                    {images.map((_, index) => (
                      <button
                        type="button"
                        key={index}
                        className={'content-thumb-dot ' + (index === safeIndex ? 'is-active' : '')}
                        aria-label={`Show slide ${index + 1} of ${images.length}`}
                        aria-current={index === safeIndex ? 'true' : undefined}
                        onClick={event => showImage(index, event)}
                      />
                    ))}
                  </div>
                )}
                <span className={'content-thumb-counter ' + (!hasLoadedCarousel ? 'is-static' : '')}>{safeIndex + 1}/{hasLoadedCarousel ? images.length : mediaCount}</span>
              </>
            ) : hasCarousel ? (
              <span className="content-thumb-counter is-static">1/{mediaCount}</span>
            ) : null}
          </>
        ) : (
          <div className="content-thumb-text">
            <div className="content-thumb-kicker">{item.type}</div>
            <p>{item.previewText}</p>
          </div>
        )}
        <div className="content-platform-strip"><TargetStatusPills item={item} /></div>
        <span className={'chip content-status ' + statusClass(item.status)}>{statusLabel(item.status)}</span>
      </div>
      <div className="content-card-body-v2">
        <div className="content-title-v2">{item.title}</div>
        {generating && (
          <div className="content-generation-inline">
            <div className="row between" style={{ gap: 8 }}>
              <span className="caption">{generationStageLabel(item.generationStage)}</span>
              <span className="caption">{clampProgress(item.generationProgress)}%</span>
            </div>
            <div className="dt-progress"><div className="dt-progress-fill" style={{ width: `${clampProgress(item.generationProgress)}%` }} /></div>
          </div>
        )}
        <div className="content-card-meta-row">
          <span className="caption">{item.type} · {item.date}</span>
          <MetricPair metrics={item.metrics} status={item.status} />
        </div>
        {item.publishMeta && <div className="caption" style={{ marginTop: 6 }}>{item.publishMeta}</div>}
      </div>
    </div>
  );
}

function ContentListThumb({ item }) {
  const imageUrl = item.imageUrl || fallbackCarouselImages(item)[0];
  if (imageUrl) {
    return <img className="content-list-thumb" src={imageUrl} alt="" loading="lazy" decoding="async" width="48" height="48" />;
  }
  return (
    <span className={'content-list-thumb content-list-thumb-fallback ' + (item.status === 'generating' ? 'is-generating' : '')}>
      <Platform name={item.platform} size={22} />
    </span>
  );
}

function fallbackCarouselImages(item) {
  if (!isCarouselItem(item)) return [];
  const count = clampCarouselCount(Number(item.mediaPreview?.count || 0) || inferredCarouselCount(item));
  return Array.from({ length: count }, (_, index) => carouselPreviewSvgUrl(item, index, count));
}

function isCarouselItem(item) {
  const raw = item?.raw || {};
  const format = raw.contentItem?.format || raw.format || item?.format || item?.type;
  return String(format || '').toLowerCase().includes('carousel');
}

function inferredCarouselCount(item) {
  const slides = item?.raw?.carouselSlides || item?.raw?.slides;
  if (Array.isArray(slides) && slides.length) return slides.length;
  return 5;
}

function clampCarouselCount(value) {
  return Math.max(2, Math.min(5, Number.isFinite(value) ? value : 5));
}

function carouselPreviewSvgUrl(item, index, count) {
  const title = String(item?.title || 'Carousel').replace(/\s+/g, ' ').trim();
  const body = String(item?.previewText || item?.raw?.activeVersion?.caption || '').replace(/\s+/g, ' ').trim();
  const accent = ['#7c3aed', '#2563eb', '#0891b2', '#db2777', '#ea580c'][index % 5];
  const shortTitle = escapeSvgText(truncateSvgText(index === 0 ? title : body || title, 42));
  const shortBody = escapeSvgText(truncateSvgText(index === 0 ? body : title, 72));
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1350" viewBox="0 0 1080 1350"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="#0f172a"/><stop offset="0.52" stop-color="#111827"/><stop offset="1" stop-color="${accent}"/></linearGradient><radialGradient id="r" cx="24%" cy="12%" r="72%"><stop stop-color="rgba(255,255,255,.28)"/><stop offset="1" stop-color="rgba(255,255,255,0)"/></radialGradient></defs><rect width="1080" height="1350" rx="54" fill="url(#g)"/><rect width="1080" height="1350" rx="54" fill="url(#r)"/><circle cx="860" cy="210" r="180" fill="rgba(255,255,255,.08)"/><circle cx="126" cy="1120" r="250" fill="rgba(255,255,255,.06)"/><text x="82" y="130" fill="rgba(255,255,255,.78)" font-family="Inter, Arial, sans-serif" font-size="34" font-weight="700">DATHENT</text><text x="82" y="630" fill="#fff" font-family="Inter, Arial, sans-serif" font-size="72" font-weight="800">${shortTitle}</text><text x="82" y="742" fill="rgba(255,255,255,.78)" font-family="Inter, Arial, sans-serif" font-size="38" font-weight="500">${shortBody}</text><text x="82" y="1220" fill="rgba(255,255,255,.86)" font-family="Inter, Arial, sans-serif" font-size="34" font-weight="700">${index + 1}/${count}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function truncateSvgText(value, max) {
  return value.length > max ? `${value.slice(0, max - 1)}...` : value;
}

function escapeSvgText(value) {
  return value.replace(/[&<>"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[char]));
}

function preloadCarouselNeighbors(images, index) {
  if (!Array.isArray(images) || images.length <= 1) return undefined;
  let cancelled = false;
  const run = () => {
    if (cancelled) return;
    [index - 1, index + 1].forEach(value => {
      const next = images[(value + images.length) % images.length];
      if (!next) return;
      const image = new Image();
      image.decoding = 'async';
      image.src = next;
    });
  };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(run, { timeout: 700 });
    return () => {
      cancelled = true;
      window.cancelIdleCallback?.(handle);
    };
  }
  const handle = window.setTimeout(run, 120);
  return () => {
    cancelled = true;
    window.clearTimeout(handle);
  };
}

function PlatformChips({ platforms = [], compact = false }) {
  const values = normalizePlatformList(platforms);
  const visible = values.slice(0, compact ? 4 : 5);
  const extra = values.length - visible.length;
  return (
    <span className={'content-platform-chips ' + (compact ? 'is-compact' : '')}>
      {visible.map(platform => <span key={platform} className="content-platform-chip"><Platform name={platform} size={compact ? 20 : 24} /><span>{platformLabel(platform)}</span></span>)}
      {extra > 0 && <span className="content-platform-extra">+{extra}</span>}
    </span>
  );
}

function TargetStatusPills({ item, compact = false }) {
  const targetByPlatform = buildTargetByPlatform(item);
  const platforms = normalizePlatformList(item.platforms);
  if (!platforms.length) return compact ? <span className="content-target-pills is-compact" /> : null;
  const visible = platforms.slice(0, compact ? 4 : 5);
  const extra = platforms.length - visible.length;
  return (
    <span className={'content-target-pills ' + (compact ? 'is-compact' : '')}>
      {visible.map(platform => {
        const target = targetByPlatform.get(platform);
        const status = targetStatusForPlatform(item, target);
        const tooltip = targetTooltipForPlatform(item, target, platform);
        return (
          <span key={platform} className={'content-target-pill status-' + status} title={tooltip}>
            <Platform name={platform} size={compact ? 16 : 18} />
            <span className={'content-target-dot status-' + status} aria-hidden="true" />
          </span>
        );
      })}
      {extra > 0 && <span className="content-target-extra">+{extra}</span>}
    </span>
  );
}

function buildTargetByPlatform(item) {
  const map = new Map();
  const targets = item?.publishSummary?.targets;
  if (Array.isArray(targets)) {
    targets.forEach(target => {
      const platform = target?.platform === 'twitter' ? 'x' : target?.platform;
      if (!platform) return;
      const existing = map.get(platform);
      if (!existing || targetPriority(target) > targetPriority(existing)) {
        map.set(platform, target);
      }
    });
  }
  return map;
}

function targetPriority(target) {
  if (target?.status === 'published') return 4;
  if (['failed', 'needs_reconnect', 'needs_manual_action'].includes(target?.status)) return 3;
  if (['publishing', 'queued', 'pending'].includes(target?.status)) return 2;
  return 1;
}

function targetStatusForPlatform(item, target) {
  if (target?.status === 'published') return 'published';
  if (['failed', 'needs_reconnect', 'needs_manual_action'].includes(target?.status)) return 'failed';
  if (['queued', 'publishing'].includes(target?.status)) return 'publishing';
  if (target?.status === 'pending') return 'scheduled';
  if (item?.status === 'published') return 'published';
  if (item?.status === 'failed') return 'failed';
  if (item?.status === 'scheduled') return 'scheduled';
  if (item?.status === 'generating') return 'generating';
  return 'idle';
}

function targetTooltipForPlatform(item, target, platform) {
  const platformText = platformLabel(platform);
  if (target?.status === 'published') {
    const when = target.publishedAt ? new Date(target.publishedAt).toLocaleString() : '';
    return `${platformText}: Published${when ? ' · ' + when : ''}`;
  }
  if (['failed', 'needs_reconnect', 'needs_manual_action'].includes(target?.status)) {
    return `${platformText}: ${target.lastErrorMessage || 'Publish failed'}`;
  }
  if (['queued', 'publishing'].includes(target?.status)) return `${platformText}: Publishing now`;
  if (target?.status === 'pending') {
    const when = target.scheduledAt ? new Date(target.scheduledAt).toLocaleString() : '';
    return `${platformText}: Scheduled${when ? ' · ' + when : ''}`;
  }
  return platformText;
}

function MetricPair({ metrics, status, compact = false }) {
  const showZeros = status === 'published' || metrics?.available;
  if (!showZeros) return compact ? <span className="content-metrics is-empty" /> : null;
  const reactions = metrics?.reactions ?? metrics?.reactionsTotal ?? 0;
  const comments = metrics?.comments ?? 0;
  const viewsAvailable = metrics?.viewsAvailable !== false;
  const viewsPending = metrics?.viewsPending === true;
  const viewsTitle = viewsAvailable
    ? (viewsPending ? 'Some Instagram views are still pending' : undefined)
    : (viewsPending ? 'Views pending from Instagram' : 'Views unavailable');
  return (
    <span className={'content-metrics ' + (compact ? 'is-compact' : '')}>
      <span title={viewsTitle}><Icon name="eye" size={compact ? 13 : 14} /> {viewsAvailable ? formatMetric(metrics?.views || 0) : '--'}</span>
      <span><Icon name="heart" size={compact ? 13 : 14} /> {formatMetric(metrics?.likes || 0)}</span>
      {reactions > 0 && <span><Icon name="flame" size={compact ? 13 : 14} /> {formatMetric(reactions)}</span>}
      {!compact && comments > 0 && <span><Icon name="msg" size={14} /> {formatMetric(comments)}</span>}
    </span>
  );
}

function EmptyContent({ onCreate, query }) {
  return (
    <div className="card content-empty-v2">
      <div className="empty-illustration-v2">
        <Icon name="grid" size={44} />
      </div>
      <h2 className="h2" style={{ marginBottom: 8 }}>{query ? 'No matching content' : 'No content yet'}</h2>
      <p className="body" style={{ color: 'var(--text-3)', marginBottom: 24 }}>{query ? 'Try another search or clear filters.' : 'Create your first post with AI or write one manually.'}</p>
      <div className="row" style={{ justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-primary btn-lg" onClick={onCreate}><Icon name="sparkles" size={14} /> Generate with AI</button>
        <button className="btn btn-secondary btn-lg" onClick={onCreate}>Write manually</button>
      </div>
    </div>
  );
}

function statusLabel(status) {
  if (status === 'generating') return 'Generating';
  if (status === 'partial_failed') return 'Partial failure';
  if (status === 'planned') return 'Needs approval';
  if (status === 'ready') return 'Ready';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function statusClass(status) {
  if (status === 'published') return 'chip-green';
  if (status === 'scheduled') return 'chip-blue';
  if (status === 'generating') return 'chip-blue';
  if (status === 'planned') return 'chip-amber';
  if (status === 'ready') return 'chip-amber';
  if (status === 'failed') return 'chip-red';
  if (status === 'partial_failed') return 'chip-red';
  return '';
}

function generationStageLabel(stage) {
  if (stage === 'drafting') return 'Drafting copy';
  if (stage === 'queued') return 'Queued';
  if (stage === 'failed') return 'Failed';
  return 'Preparing';
}

function clampProgress(value) {
  return Math.max(5, Math.min(100, Number(value) || 5));
}

function normalizePlatformList(platforms) {
  const values = Array.isArray(platforms) ? platforms : [];
  return [...new Set(values.map(platform => platform === 'twitter' ? 'x' : platform).filter(Boolean))];
}

function platformLabel(platform) {
  const labels = { instagram: 'Instagram', threads: 'Threads', telegram: 'Telegram', x: 'X', twitter: 'X', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook' };
  return labels[platform] || platform;
}

function formatMetric(value) {
  const numeric = Number(value) || 0;
  if (numeric >= 1000000) return `${(numeric / 1000000).toFixed(numeric >= 10000000 ? 0 : 1)}M`;
  if (numeric >= 1000) return `${(numeric / 1000).toFixed(numeric >= 10000 ? 0 : 1)}k`;
  return String(numeric);
}
